<template>
    <div>
        <b-modal
            id="postInvoiceModal"
            ref="postInvoiceModal"
            title-tag="h2"
            title="Post Invoice"
            :lazy="true"
            :okTitle="okTitle"
            no-close-on-backdrop
            no-close-on-esc
        >
            <form name="post-invoice" v-if="postDetails">
                <b-alert
                    variant="danger"
                    :show="
                        postDetails.over_refund || postDetails.message !== ''
                    "
                >
                    {{ postDetails.message }}
                </b-alert>
                <b-alert variant="info" :show="refundCreditMessage !== ''">
                    {{ refundCreditMessage }}
                </b-alert>
                <b-form-group
                    label-for="overpaid"
                    label="Overpaid:"
                    label-cols="4"
                    class="d-flex align-items-center"
                >
                    <span>{{ postDetails.amount }}</span>
                </b-form-group>

                <b-form-group
                    v-if="!postDetails.only_credits"
                    label-for="issue"
                    label="Issue:"
                    label-cols="4"
                    class="d-flex align-items-center"
                >
                    <b-input
                        v-if="isPreparingIssues"
                        value="Loading..."
                        disabled
                    />
                    <select
                        v-else
                        v-model="selectedIssue"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('issue') }"
                        id="issue"
                        name="issue"
                        ref="issue"
                        class="form-control"
                        aria-invalid="false"
                    >
                        <optgroup
                            v-for="group in isssueGroups"
                            :label="group.label"
                            :key="group.label"
                        >
                            <option
                                v-for="option in group.options"
                                :key="option.id"
                                :value="option.id"
                            >
                                {{ option.name }}
                            </option>
                        </optgroup>
                    </select>
                </b-form-group>

                <b-form-group
                    :label-for="`terminal`"
                    label-cols="4"
                    label="Terminal:"
                    v-if="isRequireTerminal"
                >
                    <b-form-select
                        id="terminal"
                        v-model="terminal"
                        v-validate="'excluded:0'"
                        :class="{ 'border-danger': errors.has('terminal') }"
                        name="terminal"
                    >
                        <option
                            v-for="(value, key) in postDetails.cc_terminal"
                            :key="key"
                            :value="key"
                            >{{ value }}</option
                        >
                    </b-form-select>
                </b-form-group>
            </form>
            <template v-slot:modal-footer>
                <loader v-if="isLoading" :margin="1" />
                <modal-footer
                    v-else
                    :isDisabled="isOkDisabled"
                    :ok-title="okTitle"
                    @ok-action="proceed"
                    @cancel-action="close"
                />
            </template>
        </b-modal>
        <modal-refund-approval
            ref="modalRefundApproval"
            @proceed="postInvoice"
        />
    </div>
</template>
<script type="text/javascript">
/**
 * @name PostInvoice
 * @description post invoice modal
 * @see apps/application/controllers/billing/reopen.php
 * @see apps/application/views/staff/billing/invoice/reopen/post_confirm.php
 */

import InvoiceGeneralService from "@/services/invoice/invoice-general";
import PaymentDialogHelper from "@/module/payment/helper.js";
import Loader from "@/module/commons/loader";

const REFUND_TO_TERMINAL_STRIPE = "refund_to_terminal";

export default {
    mixins: [PaymentDialogHelper],
    components: {
        Loader,
        modalFooter: () => import("@/components/elements/modal-footer"),
        ModalRefundApproval: () =>
            import("@/components/billing-credit/modal-refund-approval")
    },
    computed: {
        isOkDisabled() {
            // just incase, will need to prevent proceed from click because errors bag didnt capture on first load
            return (
                (!this.selectedIssue || this.selectedIssue == -1) &&
                !this.postDetails.only_credits
            );
        },
        isRequireTerminal() {
            if (!this.selectedIssue) {
                return false;
            }
            return (
                this.selectedIssue === REFUND_TO_TERMINAL_STRIPE ||
                this.selectedIssue === this.cardconnectTerminalRef
            );
        },
        refundCreditMessage() {
            let issue = this.selectedIssue;
            let message = "";

            if (issue === "refund_to_card") {
                message = this.postDetails?.credit_amounts?.refund_to_card?.message ?? ""; // prettier-ignore
            } else if (issue === REFUND_TO_TERMINAL_STRIPE) {
                message = this.postDetails?.credit_amounts?.refund_to_terminal?.message ?? ""; // prettier-ignore
            }
            return message;
        }
    },
    data() {
        return {
            postDetails: null,
            isPreparingIssues: true,
            isssueGroups: [],
            selectedIssue: null,
            isLoading: false,
            okTitle: "Save",
            terminalOptions: [],
            terminal: null,
            creditAmounts: [],
            cardconnectTerminalRef: null
        };
    },
    methods: {
        open(res) {
            this.postDetails = res;
            this.okTitle = res.over_refund ? "Proceed" : "Save";
            this.terminalOptions = res?.cc_terminal ?? {};
            this.terminal = Object.keys(this.terminalOptions)[0] ?? null; // select first option as default
            this.generateIssueOptions(res);
            this.$refs.postInvoiceModal.show();
        },
        async generateIssueOptions(res) {
            this.isPreparingIssues = true;

            let rawOptions = res?.options ?? {};
            let groups = [];
            let refundTypes = res?.refund_types ?? {};
            let refundOptions = [];

            // default - credit
            groups.push({
                label: "Credit",
                options: [
                    {
                        id: 1,
                        name: "Issue credit"
                    }
                ]
            });

            // integrated payments
            for (let key in refundTypes) {
                let label = refundTypes[key];

                // register cardconnect terminal id (if any)
                if (this._checkPaymentName(label, "terminal", "CardConnect")) {
                    this.cardconnectTerminalRef = key;
                }

                refundOptions.push({
                    id: key,
                    name: refundTypes[key]
                });
            }

            // others options
            for (const key in rawOptions) {
                let label = rawOptions[key];

                // check duplicate
                let existed = refundOptions.find(obj => obj.id === key);
                if (existed) {
                    continue;
                }

                // skip cardconnect key-in, cardconnect card-on-file
                if (this._checkPaymentName(label, "keyed-in", "cardconnect")) {
                    continue;
                }
                if (this._checkPaymentName(label, "file", "CardConnect")) {
                    continue;
                }

                refundOptions.push({
                    id: key,
                    name: label
                });
            }

            if (refundOptions.length > 0) {
                groups.push({
                    label: "Refund",
                    options: refundOptions
                });
            }

            this.selectedIssue = 1;
            this.isssueGroups = groups;
            this.isPreparingIssues = false;
        },
        close() {
            this.isLoading = false;
            this.$refs.postInvoiceModal.hide();
        },
        proceed() {
            let params = {
                invoice_id: this.postDetails.invoice_id,
                refund_type: this.selectedIssue,
                refund_total: this.postDetails.amount_raw,
                business_id: this.$root.setting.business_id,
                terminal: this.isRequireTerminal ? this.terminal : null,
                refund_transaction_id: this.postDetails?.interac_transaction_id ?? null, // prettier-ignore
                refund_object: null // for stripe interac terminal
            };

            // Interac (Stripe) via terminal
            if (this.selectedIssue === REFUND_TO_TERMINAL_STRIPE) {
                this.proceedInteracStripe(params);
                return;
            }

            // blind refund CConnect
            if (this.isRequireTerminal) {
                this.$refs["modalRefundApproval"].open(params);
                return;
            }

            this.postInvoice(params);
            this.isLoading = true;
        },
        async postInvoice(params) {
            this.$refs.modalRefundApproval.close();

            try {
                this.isLoading = true;
                let res = await InvoiceGeneralService.postInvoiceReopen(params);
                let response = res?.response;

                if (res?.status === 1) {
                    throw new Error("Error: Invoice not found");
                }

                if (response?.code === 400) {
                    throw new Error(response?.message);
                }

                this._message(response?.message ?? "Success");
                this.$emit("reload");
                this.close();
            } catch (error) {
                this._message(error.message, "danger");
                this.isLoading = false;
            }
        },
        async proceedInteracStripe(params) {
            try {
                this.isLoading = true;
                let res = await this._proceedStripeInteracRefund({
                    amount: params.refund_total,
                    reader_id: params.terminal,
                    transaction_id: params.refund_transaction_id
                });

                if (res.state !== 0) {
                    throw new Error(res.message);
                }

                params.refund_object = res.refund;
                this.postInvoice(params);
            } catch (error) {
                this.isLoading = false;
                this._message(error.message, "danger");
            }
        }
    }
};
</script>
